import { useHead } from '#imports'

interface FAQItem {
  id: string | number
  question: string
  answer: string
}

/**
 * Generates JSON-LD schema for FAQ pages
 * @param questions Array of FAQ questions and answers
 */
export function generateFAQPageSchema(questions: FAQItem[]): null | object {
  if (!questions || questions.length === 0) return null

  // Ensure we have unique questions by using the ID as a key
  const uniqueQuestions = questions.reduce(
    (acc: Record<string, FAQItem>, item) => {
      const key = String(item.id)
      if (!acc[key]) {
        acc[key] = item
      }
      return acc
    },
    {}
  )

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: Object.values(uniqueQuestions).map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }
}

/**
 * Adds FAQ schema to the page head
 * @param questions Array of FAQ questions and answers
 */
export function useFAQPageSchema(questions: FAQItem[]): void {
  if (!questions || questions.length === 0) return

  const schemaData = generateFAQPageSchema(questions)

  if (schemaData) {
    useHead({
      script: [
        {
          key: 'faq-schema',
          type: 'application/ld+json',
          innerHTML: JSON.stringify(schemaData),
          tagPriority: 1,
        },
      ],
    })
  }
}
